/**
 * A very small rigid-body solver for the profile's game bubbles: gravity,
 * walls, and circle-against-circle collisions. Nothing else.
 *
 * Hand-rolled rather than pulled from a physics engine because the whole
 * problem is under ten circles in a box. Matter.js would be ~90KB of bundle
 * for one decorative panel, and p5 would move the bubbles onto a canvas,
 * which costs the tooltips and hover states they currently get for free as
 * DOM elements.
 *
 * Everything here is pure-ish: it mutates the bodies you hand it and reads no
 * globals, so the whole thing runs and is tested without a DOM.
 *
 * The integrator is semi-implicit Euler at a fixed timestep. Fixed matters —
 * with a variable step, a dropped frame becomes a large dt, and bodies
 * tunnel straight through the floor.
 */

export const PHYSICS = {
    /**
     * px/s². Deliberately well below anything screen-realistic: the panel is
     * only ~180px tall, so a physical-looking gravity drops everything in
     * under a third of a second and the whole thing is over before you've
     * looked at it. This is slow enough to watch.
     */
    gravity: 900,
    /** How much of the approach speed survives a bounce. */
    restitution: 0.72,
    /**
     * Hard ceiling on speed, px/s.
     *
     * Bouncy contacts plus positional correction can inject energy: a body
     * wedged between two others gets separated *and* keeps its rebound, and
     * at high restitution that compounds into bubbles firing around the
     * panel. Clamping speed is what makes it safe to be bouncy — the pile
     * stays lively but can never run away.
     */
    maxSpeed: 1600,
    /**
     * Air drag per second.
     *
     * Carries most of the burden of stopping the pile, precisely so friction
     * doesn't have to. Friction high enough to settle a low-gravity heap
     * stops the bubbles rolling; drag slows everything without caring which
     * direction it's moving, so they can still roll and still come to rest.
     */
    damping: 1.0,
    /**
     * Ground friction, applied only while touching the floor.
     *
     * Low on purpose. These are circles, and circles roll — high friction
     * makes them behave like boxes, stacking in columns where they land
     * instead of spreading into the pile a heap of balls actually forms.
     */
    friction: 1.5,
    /**
     * Friction between two touching bubbles, as the fraction of their
     * sideways relative motion cancelled per pass.
     *
     * Enough that a bubble resting on another eventually stops creeping, but
     * kept small so they can still roll off each other rather than balancing
     * on top like blocks.
     */
    contactFriction: 0.05,
    /**
     * How far apart two circles can be and still count as touching, in px.
     *
     * Without this, "touching" means "currently overlapping" exactly, and a
     * pair resting against each other can fall out of that test for a single
     * step: gravity moves a settled body ~0.25px before the next pass, which
     * is enough to put it a hair past `distance >= minimum` — no collision
     * that step, so no rest-speed snap, so it keeps the fresh `g * dt` of
     * speed gravity just gave it. If the next step lands it back in contact,
     * that speed gets snapped away again, and the pair can alternate forever
     * between "touching, snapped to 0" and "not touching, carrying one
     * gravity step" without either side ever going below sleepSpeed. Found
     * via two circles of near-equal radius, which is a normal shape for
     * bubbles sized by win count — ties are common in a way they weren't when
     * size came from games played.
     *
     * Purely a velocity-resolution margin: positional correction still only
     * fires on genuine overlap (`overlap` is clamped to 0 below), so this
     * cannot push two circles that aren't actually touching apart.
     */
    contactSlop: 1,
    /**
     * A bounce slower than this is snapped to rest instead.
     *
     * Gravity adds `g * dt` (15px/s at 60fps) to a resting body every single
     * step, and restitution turns that into a permanent low-amplitude
     * shimmer. This is what actually lets the pile settle.
     */
    restSpeed: 30,
    /** Below this, nothing is moving enough to be worth another frame. */
    sleepSpeed: 4,
    /** Collision passes per step. More is stabler under a tall stack. */
    iterations: 8,
    /**
     * Upward speed given to a tapped bubble, px/s, before the headroom clamp
     * in applyJump() — so this is the hop a bubble with plenty of space above
     * it gets, not what every bubble gets.
     */
    jumpSpeed: 420,
    /** The fixed step, in seconds. */
    timestep: 1 / 60,
    /** Catch-up ceiling: more than this far behind and we drop the time. */
    maxStepsPerFrame: 5,
    /** Give-up point for settleBodies(), so it can never hang the page. */
    settleLimit: 4000,
}

/**
 * Bodies from a packed layout. They start exactly where `bubbleLayout` put
 * them and at rest, so the first thing the panel does is drop a tidy cluster
 * into a heap — which is a better entrance than starting mid-pile.
 */
export function createBodies(bubbles) {
    return (bubbles || []).map(bubble => ({
        id: bubble.id,
        r: bubble.r,
        x: bubble.x,
        y: bubble.y,
        vx: 0,
        vy: 0,
        // Mass by area. Without it every circle behaves the same weight and
        // the small ones shove the big ones around, which looks wrong.
        mass: bubble.r * bubble.r,
    }))
}

/**
 * How tall the box needs to be for the bubbles once they've fallen.
 *
 * The packed layout is a roughly round blob; a settled pile is wide and flat,
 * so sizing the panel to the cluster leaves a band of dead air above it —
 * about a third of the panel on a phone.
 *
 * Estimated from total area at 55% coverage. That's deliberately looser than
 * the ~65% random circle packing actually reaches: this is a *lower bound on
 * the box*, and a box estimated too tight leaves bubbles wedged against the
 * ceiling with nowhere to settle. Erring roomy costs a few pixels of air.
 *
 * The largest bubble is a hard floor — whatever the maths says, one bubble
 * has to fit.
 */
export function settledHeight(bubbles, width) {
    if (!bubbles || !bubbles.length || !width) return 0

    const area = bubbles.reduce((sum, b) => sum + Math.PI * b.r * b.r, 0)
    const largest = Math.max.apply(null, bubbles.map(b => b.r))

    return Math.ceil(Math.max(largest * 2, area / (width * 0.55)))
}

/**
 * Advances the world one fixed step.
 *
 * @param {object[]} bodies mutated in place
 * @param {{width: number, height: number}} bounds
 * @param {object} [options] overrides for PHYSICS
 * @returns {boolean} whether anything is still moving — false means the
 *   caller can stop asking for frames until something disturbs the pile.
 */
export function stepBodies(bodies, bounds, options) {
    const cfg = { ...PHYSICS, ...(options || {}) }
    const dt = cfg.timestep

    for (const body of bodies) {
        // Cleared each step and set by whichever contact holds this body up.
        body.supported = false
        body.vy += cfg.gravity * dt
        // Exponential drag rather than a flat subtraction, which would drive
        // slow bodies backwards.
        const drag = Math.max(0, 1 - cfg.damping * dt)
        body.vx *= drag
        body.vy *= drag
        clampSpeed(body, cfg)
        body.x += body.vx * dt
        body.y += body.vy * dt
    }

    // Contacts and walls are resolved *together*, pass by pass, rather than
    // all the contacts and then all the walls.
    //
    // That ordering matters more than it looks. With walls resolved only at
    // the end, a stack behaves like this: the pair pass equalises the two
    // bodies' velocities (correct), then the floor zeroes the bottom one — and
    // nothing ever tells the body resting on top about it. Gravity re-adds
    // fresh speed every step, the stack reaches a steady state, and it never
    // sleeps. Interleaving lets the floor's correction propagate up
    // through the contacts in the passes that follow.
    //
    // Walls still run last within the final pass, so a body shoved out of the
    // box by a contact is always back inside before anything is painted.
    for (let pass = 0; pass < cfg.iterations; pass++) {
        resolvePairs(bodies, cfg)
        for (const body of bodies) resolveWalls(body, bounds, cfg)
    }

    for (const body of bodies) clampSpeed(body, cfg)

    // Something is still moving if it's fast, *or* if it isn't resting on
    // anything.
    //
    // The support test is not optional. At the apex of a bounce a body's
    // velocity passes through zero, and a speed-only check calls that asleep
    // — the whole pile freezes in mid-air and never comes down. (It survived
    // a gentler restitution only because the apex was small enough to fall
    // inside the tolerance, which is exactly the kind of accident that breaks
    // the moment a constant is tuned.)
    return bodies.some(body =>
        !body.supported ||
        Math.abs(body.vx) > cfg.sleepSpeed ||
        Math.abs(body.vy) > cfg.sleepSpeed)
}

/**
 * Runs the world forward until it comes to rest, without drawing anything.
 *
 * For `prefers-reduced-motion`, where the right answer is to show the result
 * rather than the journey. Cheap enough to do synchronously on mount — a few
 * hundred steps over a handful of bodies — and far better than painting the
 * packed starting positions, which leaves the bubbles hovering in mid-air in
 * an arrangement gravity was supposed to resolve.
 *
 * @returns {number} steps taken; the cap means "did not settle".
 */
export function settleBodies(bodies, bounds, options) {
    const cfg = { ...PHYSICS, ...(options || {}) }
    const limit = cfg.settleLimit

    let steps = 0
    while (steps < limit && stepBodies(bodies, bounds, options)) steps++
    return steps
}

/**
 * Advances the world by however much real time has passed, in fixed steps.
 *
 * Returns `pending` (time carried into the next call) and, separately,
 * whether any step actually **ran** and whether anything is still **moving**.
 * Those two are not the same thing, and conflating them kills the loop:
 *
 * On a 120Hz display a frame delivers 8.3ms, which is *less than one 16.7ms
 * timestep*, so every other frame legitimately runs zero steps. If the caller
 * reads "no steps ran" as "nothing is moving" it stops asking for frames, and
 * the bubbles freeze in mid-air on the second frame after they're released.
 * That is not a display quirk to shrug at — 120Hz is a phone.
 *
 * @param {number} elapsed seconds since the previous frame
 * @returns {{ pending: number, stepped: boolean, moving: boolean }}
 */
export function advance(bodies, bounds, pending, elapsed, options) {
    const cfg = { ...PHYSICS, ...(options || {}) }
    const maxSteps = cfg.maxStepsPerFrame

    // A backgrounded tab comes back with a huge gap; simulating all of it at
    // once would lock the page up catching up.
    let remaining = Math.min(
        Math.max(0, pending) + Math.max(0, elapsed),
        cfg.timestep * maxSteps,
    )

    let stepped = false
    let moving = false
    while (remaining >= cfg.timestep) {
        moving = stepBodies(bodies, bounds, options) || moving
        stepped = true
        remaining -= cfg.timestep
    }

    return { pending: remaining, stepped, moving }
}

/** Keeps a body under the speed ceiling, preserving its direction. */
function clampSpeed(body, cfg) {
    const speed = Math.sqrt(body.vx * body.vx + body.vy * body.vy)
    if (speed <= cfg.maxSpeed || speed === 0) return
    const scale = cfg.maxSpeed / speed
    body.vx *= scale
    body.vy *= scale
}

/** Separates overlapping circles and exchanges momentum along the normal. */
function resolvePairs(bodies, cfg) {
    for (let i = 0; i < bodies.length; i++) {
        for (let j = i + 1; j < bodies.length; j++) {
            const a = bodies[i]
            const b = bodies[j]

            const dx = b.x - a.x
            const dy = b.y - a.y
            const distance = Math.sqrt(dx * dx + dy * dy)
            const minimum = a.r + b.r
            // Slop widens *this* test only. Positional correction below still
            // keys off real overlap, so a pair inside the slop but not
            // actually overlapping gets its velocity resolved (and rest-speed
            // snapped) without being shoved apart.
            if (distance >= minimum + cfg.contactSlop) continue

            // Exactly concentric: choose a direction rather than dividing by
            // zero. Only the *normal* is substituted — the distance stays 0,
            // so the separation below is the full radius sum. (Faking the
            // distance instead makes `overlap` come out as 0 and the two
            // bodies stay welded together.) Fixed rather than random, so this
            // can't make the simulation unrepeatable.
            const nx = distance === 0 ? 1 : dx / distance
            const ny = distance === 0 ? 0 : dy / distance
            const invA = 1 / a.mass
            const invB = 1 / b.mass
            const invSum = invA + invB

            // Push apart in proportion to how light each one is. Clamped to 0
            // rather than allowed negative, which is what a slop-only pair
            // (touching within the margin but not truly overlapping) would
            // otherwise produce — a "correction" that pulls them together.
            const overlap = Math.max(0, minimum - distance)
            a.x -= nx * overlap * (invA / invSum)
            a.y -= ny * overlap * (invA / invSum)
            b.x += nx * overlap * (invB / invSum)
            b.y += ny * overlap * (invB / invSum)

            // Support means something is *underneath* — not merely that two
            // bodies are touching. Marking both would let a pair that grazes
            // each other in mid-air call itself settled, which is the same
            // freeze the apex-of-a-bounce case caused. `y` grows downward, so
            // a positive normal means b is below a.
            if (ny > 0.3) a.supported = true
            if (ny < -0.3) b.supported = true

            const approach = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny
            // Already separating — bouncing it again would add energy.
            if (approach > 0) continue

            // The same rest-speed snap the floor uses, and for the same
            // reason: a bubble resting *on another bubble* gets `g * dt` of
            // new approach speed every step, and a bouncy contact turns that
            // into a stack that jitters forever and never sleeps. Below the
            // threshold the contact goes fully inelastic, which cancels the
            // approach exactly instead of returning a fraction of it.
            const bounce = -approach > cfg.restSpeed ? cfg.restitution : 0

            const impulse = (-(1 + bounce) * approach) / invSum
            a.vx -= impulse * nx * invA
            a.vy -= impulse * ny * invA
            b.vx += impulse * nx * invB
            b.vy += impulse * ny * invB

            // Friction along the contact, perpendicular to the normal. This
            // is what lets a bubble come to rest *on another bubble* rather
            // than sliding off it forever.
            const tx = -ny
            const ty = nx
            const sliding = (b.vx - a.vx) * tx + (b.vy - a.vy) * ty
            const grip = (-sliding * cfg.contactFriction) / invSum
            a.vx -= grip * tx * invA
            a.vy -= grip * ty * invA
            b.vx += grip * tx * invB
            b.vy += grip * ty * invB
        }
    }
}

/** Keeps a body inside the box, bouncing off whichever side it hit. */
function resolveWalls(body, bounds, cfg) {
    const dt = cfg.timestep

    if (body.x - body.r < 0) {
        body.x = body.r
        if (body.vx < 0) body.vx = -body.vx * cfg.restitution
    } else if (body.x + body.r > bounds.width) {
        body.x = bounds.width - body.r
        if (body.vx > 0) body.vx = -body.vx * cfg.restitution
    }

    if (body.y - body.r < 0) {
        body.y = body.r
        if (body.vy < 0) body.vy = -body.vy * cfg.restitution
    } else if (body.y + body.r >= bounds.height) {
        body.supported = true
        body.y = bounds.height - body.r
        if (body.vy > 0) {
            // Snap slow bounces to rest — see PHYSICS.restSpeed.
            body.vy = body.vy > cfg.restSpeed ? -body.vy * cfg.restitution : 0
        }
        // Rolling slows on the ground, so a pile doesn't skate sideways.
        body.vx *= Math.max(0, 1 - cfg.friction * dt)
    }
}

/**
 * Makes one bubble hop, and lets it jostle whatever it lands on.
 *
 * Velocity is **assigned**, not added. Adding would let a rapid series of
 * taps accumulate into a launch that pins the bubble to the ceiling; this way
 * the tenth tap looks exactly like the first.
 *
 * Larger bubbles hop a little lower. They all hop under their own power
 * rather than being struck, so this isn't momentum — it's just that a big
 * heavy thing jumping as high as a small one looks wrong.
 *
 * @returns {boolean} whether anything actually moved
 */
export function applyJump(bodies, id, bounds, options) {
    const cfg = { ...PHYSICS, ...(options || {}) }
    const body = (bodies || []).find(candidate => candidate.id === id)
    if (!body) return false

    const largest = Math.max.apply(null, bodies.map(candidate => candidate.r))
    const heaviness = largest > 0 ? body.r / largest : 1
    let speed = cfg.jumpSpeed * (1 - heaviness * 0.25)

    // Never hop into the ceiling. The biggest bubble can have as little as
    // 40px above it in a panel this size, and a bubble that slams the top and
    // stops dead reads as a glitch rather than a jump.
    //
    // Rise is v² / 2g, so the speed that just reaches a given height is
    // sqrt(2 * g * height). Aiming at 85% of the gap leaves a visible margin.
    if (bounds && bounds.height) {
        const headroom = Math.max(0, body.y - body.r)
        speed = Math.min(speed, Math.sqrt(2 * cfg.gravity * headroom * 0.85))
    }

    body.vy = -speed
    return true
}
